/**
 * Fetch notifications from WordPress via AJAX
 * @param {string} filter - 'all' or 'unread'
 * @returns {Promise<Array>} Array of notification objects
 */
export async function fetchNotifications() {
	const formData = new FormData();
	formData.append('action', 'wpcore_get_notifications');
	formData.append('nonce', wpcore.nonce);

	try {
		const response = await fetch(wpcore.ajaxUrl, {
			method: 'POST',
			body: formData
		});
		const data = await response.json();

		if (data.success) {
			return data.data;
		} else {
			console.error('Error fetching notifications:', data.data?.message);
			if (wpcore?.alert) {
				wpcore.alert('error', wpcore.direct_t({ en: 'Notifications', fr: 'Notifications' }), data.data?.message || wpcore.direct_t({ en: 'Failed to fetch notifications', fr: '\u00c9chec de la r\u00e9cup\u00e9ration des notifications' }));
			}
			return [];
		}
	} catch (error) {
		console.error('Error fetching notifications:', error);
		if (wpcore?.alert) {
			wpcore.alert('error', wpcore.direct_t({ en: 'Notifications', fr: 'Notifications' }), wpcore.direct_t({ en: 'An error occurred while fetching notifications', fr: 'Une erreur est survenue lors de la r\u00e9cup\u00e9ration des notifications' }));
		}
		return [];
	}
}

/**
 * Mark notification as read via AJAX
 * @param {number} notificationId - Notification ID
 * @returns {Promise<boolean>} Success status
 */
export async function markNotificationAsRead(notificationId) {
	const formData = new FormData();
	formData.append('action', 'wpcore_mark_notification_read');
	formData.append('nonce', wpcore.nonce);
	formData.append('notification_id', notificationId);

	try {
		const response = await fetch(wpcore.ajaxUrl, {
			method: 'POST',
			body: formData
		});
		const data = await response.json();

		if (data.success) {
			if (wpcore?.alert) {
				wpcore.alert('success', wpcore.direct_t({ en: 'Notification', fr: 'Notification' }), data.data?.message || wpcore.direct_t({ en: 'Marked notification as read successfully', fr: 'Notification marqu\u00e9e comme lue avec succ\u00e8s' }));
			}
		} else {
			console.error('Error:', data.data?.message);
			if (wpcore?.alert) {
				wpcore.alert('error', wpcore.direct_t({ en: 'Notification', fr: 'Notification' }), data.data?.message || wpcore.direct_t({ en: 'Failed to mark notification as read', fr: '\u00c9chec du marquage de la notification comme lue' }));
			}
		}

		return data.success;
	} catch (error) {
		console.error('Error:', error);
		if (wpcore?.alert) {
			wpcore.alert('error', wpcore.direct_t({ en: 'Notification', fr: 'Notification' }), wpcore.direct_t({ en: 'An error occurred. Please try again.', fr: 'Une erreur est survenue. Veuillez r\u00e9essayer.' }));
		}
		return false;
	}
}

/**
 * Mark notification as unread via AJAX
 * @param {number} notificationId - Notification ID
 * @returns {Promise<boolean>} Success status
 */
export async function markNotificationAsUnread(notificationId) {
	const formData = new FormData();
	formData.append('action', 'wpcore_mark_notification_unread');
	formData.append('nonce', wpcore.nonce);
	formData.append('notification_id', notificationId);

	try {
		const response = await fetch(wpcore.ajaxUrl, {
			method: 'POST',
			body: formData
		});
		const data = await response.json();
		return data.success;
	} catch {
		return false;
	}
}

/**
 * Delete notification via AJAX
 * @param {number} notificationId - Notification ID
 * @returns {Promise<boolean>} Success status
 */
export async function deleteNotification(notificationId) {
	const formData = new FormData();
	formData.append('action', 'wpcore_delete_notification');
	formData.append('nonce', wpcore.nonce);
	formData.append('notification_id', notificationId);

	try {
		const response = await fetch(wpcore.ajaxUrl, {
			method: 'POST',
			body: formData
		});
		const data = await response.json();

		if (data.success) {
			if (wpcore?.alert) {
				wpcore.alert('success', wpcore.direct_t({ en: 'Notification', fr: 'Notification' }), data.data?.message || wpcore.direct_t({ en: 'Deleted notification successfully', fr: 'Notification supprim\u00e9e avec succ\u00e8s' }));
			}
		} else {
			console.error('Error:', data.data?.message);
			if (wpcore?.alert) {
				wpcore.alert('error', wpcore.direct_t({ en: 'Notification', fr: 'Notification' }), data.data?.message || wpcore.direct_t({ en: 'Failed to delete notification', fr: '\u00c9chec de la suppression de la notification' }));
			}
		}

		return data.success;
	} catch (error) {
		console.error('Error:', error);
		if (wpcore?.alert) {
			wpcore.alert('error', wpcore.direct_t({ en: 'Notification', fr: 'Notification' }), wpcore.direct_t({ en: 'An error occurred. Please try again.', fr: 'Une erreur est survenue. Veuillez r\u00e9essayer.' }));
		}
		return false;
	}
}

/**
 * Mark all notifications as read via AJAX
 * @param {string} nonce - Security nonce
 * @returns {Promise<Object>} Response object
 */
export async function markAllNotificationsAsRead(nonce) {
	const formData = new FormData();
	formData.append('action', 'wpcore_mark_all_notifications_read');
	formData.append('mark_all_read_nonce', nonce);

	try {
		const response = await fetch(wpcore.ajaxUrl, {
			method: 'POST',
			body: formData
		});

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data = await response.json();

		if (data.success) {
			if (wpcore?.alert) {
				const type = data.data?.already_read ? 'info' : 'success';
				wpcore.alert(type, wpcore.direct_t({ en: 'Notifications', fr: 'Notifications' }), data.data?.message);
			}
		} else {
			if (wpcore?.alert) {
				wpcore.alert('error', wpcore.direct_t({ en: 'Notifications', fr: 'Notifications' }), data.data?.message || wpcore.direct_t({ en: 'Failed to mark all as read', fr: '\u00c9chec du marquage de toutes les notifications comme lues' }));
			}
		}

		return data;
	} catch (error) {
		console.error('Error marking all as read:', error);
		if (wpcore?.alert) {
			wpcore.alert('error', wpcore.direct_t({ en: 'Notifications', fr: 'Notifications' }), wpcore.direct_t({ en: 'An error occurred. Please try again.', fr: 'Une erreur est survenue. Veuillez r\u00e9essayer.' }));
		}
		throw error;
	}
}
